import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ContractForm from './ContractForm';
import { shortDay } from '../../lib/licences';
import { contractsApi, errorOf, STATUS_LABEL, statusTone, nextWords, capWords, rateWords } from '../../lib/contracts';
import '../Assets.css';
import '../licences/Licences.css';
import './Contracts.css';

/**
 * THE CONTRACT TAB on a contractor's profile (120): every contract with
 * them, the active ones first - its dates, the rate today, the caps and
 * what is used - each opening its own page, and "Add a contract" for them.
 * Administrators only (the profile is), behind the Contracts switch.
 */
export default function ContractorContracts({ contractorId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [options, setOptions] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => contractsApi.forContractor(contractorId)
    .then((d) => { setData(d); setError(''); })
    .catch((e) => setError(e?.response?.status === 503 ? 'Contracts need migration 120 on this server.' : errorOf(e, 'Could not load the contracts.'))),
  [contractorId]);
  useEffect(() => { load(); }, [load]);
  const startAdding = () => {
    setAdding(true);
    if (!options) contractsApi.options().then(setOptions).catch(() => setOptions({ contractors: [], companies: [], people: [], categories: [] }));
  };
  const closeForm = useCallback(() => setAdding(false), []);

  return (
    <div className="profile-section" data-testid="contractor-contracts">
      <div className="con-bar">
        <p className="page-description" style={{ margin: 0 }}>
          {data?.contractor?.hourly_rate != null
            ? `Without a contract, work is priced at their own rate of €${Number(data.contractor.hourly_rate).toFixed(2)} an hour.`
            : 'What has been agreed with this contractor.'}
        </p>
        <button type="button" className="btn btn-primary" onClick={startAdding} disabled={Boolean(error)}>Add a contract</button>
      </div>
      {error && <p className="as-error" role="alert">{error}</p>}
      {!data && !error && <p className="as-quiet" aria-busy="true">Loading…</p>}
      {data && data.contracts.length === 0 && <p className="no-data">No contracts with them yet.</p>}
      {data && data.contracts.length > 0 && (
        <ul className="con-cards">
          {data.contracts.map((k) => {
            const nx = nextWords(k);
            const caps = [capWords(k.cap.hours, 'hours', k.currency), capWords(k.cap.amount, 'amount', k.currency)].filter(Boolean);
            return (
              <li key={k.id} className="as-panel con-card" data-contract={k.id}>
                <div className="lic-panel-head">
                  <h3 className="con-card-title"><Link to={`/manage/contracts/${k.id}`}>{k.title}</Link></h3>
                  <span className="lic-head-chips" style={{ marginTop: 0 }}>
                    <span className="as-chip" data-tone={statusTone(k.status)}>{STATUS_LABEL[k.status]}</span>
                    {nx && <span className="as-chip" data-tone={nx.tone}>{nx.text}</span>}
                  </span>
                </div>
                <dl className="as-facts">
                  <dt>Dates</dt><dd>{k.start_date ? `${shortDay(k.start_date)} – ${k.end_date ? shortDay(k.end_date) : 'open'}` : '—'}</dd>
                  {k.company_name && <><dt>For</dt><dd>{k.company_name}</dd></>}
                  {k.notice_deadline && <><dt>Notice by</dt><dd>{shortDay(k.notice_deadline)}</dd></>}
                  <dt>Rate today</dt><dd>{rateWords(k.rate_now?.default?.hourly_rate, k.currency) || 'Their own'}</dd>
                  {caps.length > 0 && <><dt>Caps</dt><dd>{caps.map((c) => <span key={c.text} className="as-chip" data-tone={c.tone} style={{ marginRight: '0.3rem' }}>{c.text}</span>)}</dd></>}
                </dl>
              </li>
            );
          })}
        </ul>
      )}
      {adding && options && (
        <ContractForm contractorId={contractorId} options={options} onClose={closeForm}
          onSaved={(saved) => { setAdding(false); navigate(`/manage/contracts/${saved.contract.id}`); }} />
      )}
    </div>
  );
}
